import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { merge } from "lodash";

import { useAppDispatch } from "app/hooks";
import { misc, neutral } from "app/themes/colors";
import CheckboxFormField from "components/checkbox-form-field";
import Dialog from "components/dialog";
import { shouldSelectInvoiceByTextContent } from "components/word-processor-field/merge-tags/merge-tags-options/patient-selected-invoice.utils";
import { getParsedWordProcessorValue } from "components/word-processor-field/word-processor-value.utils";
import {
  selectDataParamsById,
  setDataParams
} from "components/word-processor-field/word-processor.slice";
import {
  TEMPLATE_TYPE_DESIGNER_EMAIL,
  TEXT_TEMPLATE_TYPES_V3
} from "constants/templates";
import { TemplateSourceType } from "services/suno-comms/types/text-templates.type";
import { TextTemplate } from "services/types/text-templates.type";
import { wrapHtmlForIframe } from "utils/message-templates.util";
import { convertSunoPlaceholdersToMergeTags } from "utils/suno-merge-tags.util";

import ComposeTemplateDisplay from "./compose-template-display.component";
import SelectTemplateField, {
  SelectTemplateFieldRef
} from "./select-template-field.component";
import {
  type TemplateSubmitData,
  resolveSubmitTemplate
} from "./text-templates-v2.utils";

export type { TemplateSubmitData };

const TEAL = "#0F6E56";
const TEAL_HOVER = "#0C5A47";
const TEAL_FILL = "#E1F5EE";
const TEAL_BORDER = "#9FE1CB";

const sectionHeadingSx = {
  fontSize: "13px",
  fontWeight: 500,
  color: neutral[600],
  lineHeight: 1.4
} as const;

const cardSx = {
  background: "#fff",
  border: `1px solid ${neutral[200]}`,
  borderRadius: "10px",
  p: 2,
  display: "flex",
  flexDirection: "column"
} as const;

type TextTemplatesDialogV2Props = {
  readonly fieldIdRef: React.MutableRefObject<string | undefined>;
  readonly allowInsertMergeTagsValuesFeature?: boolean;
  readonly templateTypes?: number[];
  readonly show?: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (values: TemplateSubmitData) => void;
  readonly disableComposeEmail?: boolean;
  /** When set, raises the dialog above nested parents (e.g. Insert Scribe). */
  readonly dialogZIndex?: number;
  /** Backend to read templates from. Omitted ⇒ "default" (main backend). */
  readonly templateSource?: TemplateSourceType;
};

const DEFAULT_VALUES = {
  template: null,
  needReplace: false,
  composedTemplate: ""
};

export default function TextTemplatesDialogV2({
  fieldIdRef,
  allowInsertMergeTagsValuesFeature,
  templateTypes,
  show,
  disableComposeEmail,
  onClose,
  onSubmit,
  dialogZIndex,
  templateSource
}: TextTemplatesDialogV2Props) {
  const dataParams = useSelector((state: any) =>
    selectDataParamsById(state, String(fieldIdRef.current))
  );
  const dispatch = useAppDispatch();
  const { zIndex } = useTheme();

  const [composeTemplateEnabled, setComposeTemplateEnabled] = useState(false);
  const selectTemplateFieldRef = useRef<SelectTemplateFieldRef>(null);

  useEffect(() => {
    if (show) {
      setComposeTemplateEnabled(false);
    }
  }, [show]);

  const defaultValues: TemplateSubmitData = useMemo(() => {
    return merge({}, DEFAULT_VALUES);
  }, []);

  const formMethods = useForm<TemplateSubmitData>({
    mode: "onChange",
    defaultValues
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { isValid }
  } = formMethods;

  const [selectedTemplate] = watch<["template"]>(["template"]);

  const isDesignerEmailTemplate =
    selectedTemplate?.type === TEMPLATE_TYPE_DESIGNER_EMAIL;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (show && isDesignerEmailTemplate) {
      setValue("needReplace", true);
    }
  }, [show, isDesignerEmailTemplate, setValue]);

  const [expanded, setExpanded] = useState<string[]>([]);

  const onExpand = useCallback((nodeIds: string[]) => {
    setExpanded(nodeIds);
  }, []);

  const onSelect = useCallback(
    (template: TextTemplate | null) => {
      setValue("template", template);
    },
    [setValue]
  );

  const [search, setSearch] = useState("");

  const selectedTemplateText = useMemo(() => {
    return selectedTemplate?.text ?? "";
  }, [selectedTemplate?.text]);

  const saleId = useMemo(() => {
    return dataParams.saleId;
  }, [dataParams.saleId]);

  const shouldSelectInvoice = useMemo(() => {
    return Boolean(
      allowInsertMergeTagsValuesFeature &&
        show &&
        selectedTemplate &&
        !saleId &&
        shouldSelectInvoiceByTextContent(selectedTemplateText)
    );
  }, [
    allowInsertMergeTagsValuesFeature,
    show,
    selectedTemplate,
    saleId,
    selectedTemplateText
  ]);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleBeforeSubmit = useCallback(
    async (values: TemplateSubmitData) => {
      onSubmit(resolveSubmitTemplate(values, composeTemplateEnabled));
      reset(defaultValues);
    },
    [composeTemplateEnabled, defaultValues, onSubmit, reset]
  );

  const handleComposeTemplateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setComposeTemplateEnabled(event.target.checked);
      if (event.target.checked) {
        // Focus search field when compose template mode is enabled
        // Use a longer timeout to ensure DOM has fully updated
        setTimeout(() => {
          selectTemplateFieldRef.current?.focusSearchField();
        }, 100);
      }
    },
    []
  );

  const handleAddToComposedTemplate = useCallback(
    (text: string) => {
      const currentValue = getValues("composedTemplate") ?? "";
      setValue("composedTemplate", `${currentValue} ${text}`);
    },
    [getValues, setValue]
  );

  return (
    <Dialog
      title={`Select a template${
        templateTypes?.length
          ? " (type " +
            templateTypes
              .map((type) => TEXT_TEMPLATE_TYPES_V3.getFromCode(type))
              .join(", ") +
            ") "
          : ""
      }`}
      subtitle="Choose, preview, and compose template content before inserting it."
      titleTextSx={{ fontSize: "17px", fontWeight: 500, color: neutral[800] }}
      minWidth={1800}
      onClose={() => {
        reset(defaultValues);
        onClose();
      }}
      open={Boolean(show)}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "flex-end",
          p: 3,
          flexDirection: "column",
          backgroundColor: neutral[50]
        }
      }}
      sx={{
        zIndex: dialogZIndex ?? zIndex.snackbar + 2,
        "& .MuiDialog-paper": {
          width: "calc(100vw - 240px)",
          minWidth: 0,
          maxWidth: "1800px"
        }
      }}
    >
      <Box sx={{ width: "100%" }}>
        {!disableComposeEmail && (
          <Stack direction="row" alignItems="center" sx={{ mb: 2, mt: -1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={composeTemplateEnabled}
                  onChange={handleComposeTemplateChange}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: TEAL },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: TEAL
                    }
                  }}
                />
              }
              label="Compose Template Mode"
            />
          </Stack>
        )}

        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            columnGap: 2.5,
            height: "700px"
          }}
        >
          <SelectTemplateField
            ref={selectTemplateFieldRef}
            templateTypes={templateTypes}
            templateSource={templateSource}
            search={search}
            setSearch={handleSearch}
            selected={
              selectedTemplate?.id ? `t${selectedTemplate?.id}` : undefined
            }
            expanded={expanded}
            onSelect={onSelect}
            onExpand={onExpand}
            composeTemplateEnabled={composeTemplateEnabled}
            onAddToComposedTemplate={handleAddToComposedTemplate}
          />
          {composeTemplateEnabled && (
            <Box sx={{ ...cardSx, width: 620, height: "100%" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.5, flexShrink: 0 }}
              >
                <Stack direction="row" alignItems="center" columnGap={0.75}>
                  <EditOutlinedIcon
                    sx={{ fontSize: 16, color: neutral[600] }}
                  />
                  <Typography sx={sectionHeadingSx}>Compose editor</Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setValue("composedTemplate", "");
                  }}
                  sx={{
                    height: "28px",
                    color: TEAL,
                    borderColor: TEAL_BORDER,
                    "&:hover": {
                      borderColor: TEAL,
                      backgroundColor: TEAL_FILL
                    }
                  }}
                >
                  Clear
                </Button>
              </Stack>
              <Box sx={{ flex: 1, minHeight: 0, height: "100%" }}>
                <ComposeTemplateDisplay control={control} />
              </Box>
            </Box>
          )}
          <Box sx={{ ...cardSx, flex: 1, minWidth: 0, height: "100%" }}>
            <Stack
              direction="row"
              alignItems="center"
              columnGap={0.75}
              sx={{ mb: 1.5, flexShrink: 0 }}
            >
              <VisibilityOutlinedIcon
                sx={{ fontSize: 16, color: neutral[600] }}
              />
              <Typography sx={sectionHeadingSx}>
                Selected template preview
              </Typography>
            </Stack>
            {!selectedTemplate && (
              <Stack
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  borderRadius: "8px",
                  border: `1px dashed ${neutral[300]}`,
                  backgroundColor: neutral[50],
                  color: neutral[600]
                }}
              >
                <TextSnippetIcon sx={{ fontSize: 44, color: neutral[400] }} />
                <Typography variant="body3" sx={{ mt: 1, color: neutral[600] }}>
                  Select a template to preview it here
                </Typography>
              </Stack>
            )}
            {selectedTemplate && (
              <Stack
                sx={{
                  flex: 1,
                  minHeight: 0,
                  position: "relative",
                  background: "#fff",
                  border: `1px solid ${TEAL_BORDER}`,
                  borderRadius: "8px",
                  overflow: "auto",
                  p: 1.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: TEAL,
                    borderBottom: `1px solid ${neutral[200]}`,
                    pb: 0.5,
                    mb: 0.5
                  }}
                >
                  {selectedTemplate?.name ?? ""}
                </Typography>
                <Box
                  component="iframe"
                  srcDoc={wrapHtmlForIframe(
                    convertSunoPlaceholdersToMergeTags(
                      getParsedWordProcessorValue(selectedTemplate?.text ?? "")
                        .html ?? ""
                    ),
                    { wordBreak: "break-all" }
                  )}
                  sandbox="allow-same-origin"
                  sx={{
                    border: "none",
                    width: "100%",
                    flex: 1
                  }}
                />
              </Stack>
            )}
          </Box>
        </Box>
      </Box>

      <DialogActions
        sx={{
          position: "sticky",
          width: "100%",
          mt: 2,
          pt: 2,
          pr: 3,
          backgroundColor: "#fff",
          borderTop: `1px solid ${neutral[200]}`
        }}
      >
        {shouldSelectInvoice && (
          <Box mt={1} width="950px">
            <Stack direction="row" alignItems="center" spacing={1} ml={40}>
              <WarningIcon sx={{ fontSize: "22px", color: misc.warningText }} />
              <Typography
                fontWeight={700}
                fontSize="16px"
                color={misc.warningText}
                sx={{ pb: 0.3 }}
              >
                Please select an invoice before inserting this template content
              </Typography>
              <Button
                size="small"
                variant="contained"
                sx={{ ml: 4 }}
                onClick={() => {
                  dispatch(
                    setDataParams({
                      id: fieldIdRef.current,
                      openSelectSaleDialog: true
                    })
                  );
                }}
              >
                Select Invoice…
              </Button>
            </Stack>
          </Box>
        )}
        <FormControlLabel
          label="Replace existing content"
          control={
            <CheckboxFormField
              name="needReplace"
              control={control}
              testId="need-replace"
              disabled={isDesignerEmailTemplate}
              sx={{ "&.Mui-checked": { color: TEAL } }}
            />
          }
        />

        <Button
          disabled={!isValid || shouldSelectInvoice}
          variant="contained"
          size="small"
          sx={{
            mr: -1,
            backgroundColor: TEAL,
            "&:hover": { backgroundColor: TEAL_HOVER },
            "&.Mui-disabled": {
              backgroundColor: neutral[200],
              color: neutral[500]
            }
          }}
          onClick={handleSubmit(handleBeforeSubmit)}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
}
